import React, { useState } from "react"
import moment from "moment"
import { Modal } from "reactstrap"

import { SnapshotIcon } from "./ChildSnapshot"
import type { AiLessonPlanningController } from "./useAiLessonPlanning"
import type {
  AiPlanTarget,
  AiRecommendation,
  AiSubject,
} from "./aiLessonPlanningTypes"
import "./ai-lesson-planning.css"

const SKIP_REASONS = ["Already covered", "Too easy", "Too hard", "Not relevant"]

const REASON_ORDER = ["progress", "preference", "relearn"]

const REASON_META: Record<string, { label: string; hint: string }> = {
  progress: { label: "Progress", hint: "Next skills to move forward" },
  preference: {
    label: "Preference",
    hint: "Formats and topics this student uses often",
  },
  relearn: { label: "Relearn", hint: "Skipped or older skills to revisit" },
  performance: {
    label: "Performance",
    hint: "Skills where recent scores are low",
  },
}

const SUBJECT_LABEL: Record<AiSubject, string> = {
  math: "Maths",
  english: "English",
}

type ReasonFilter = "all" | "progress" | "preference" | "relearn" | "performance"
type SubjectFilter = "both" | AiSubject

function subjectsLabel(subjects: AiSubject[]): string {
  return subjects.map(subject => SUBJECT_LABEL[subject]).join(" & ")
}

function contentTypeLabel(contentType: string): string {
  const value = (contentType || "").trim().toUpperCase()
  if (value === "QUIZ" || value === "ONLINE") return "Online Activity"
  if (value === "DOCUMENT" || value === "WORKSHEET" || value === "PDF") return "PDF"
  return contentType
}

function itemMeta(item: AiRecommendation): string {
  const path = [item.domain, item.strand].filter(Boolean).join(" › ")
  const note =
    item.reasonNotes && item.reasonNotes.length > 0 ? item.reasonNotes[0] : null
  return [path, item.year ? `Y${item.year}` : null, note, item.sourceId]
    .filter(Boolean)
    .join(" · ")
}

export const SparkleIcon = ({
  size = 16,
  color = "#E0A526",
}: {
  size?: number
  color?: string
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
  </svg>
)

const CloseIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
)

const ReasonBadge = ({
  item,
  small,
}: {
  item: AiRecommendation
  small?: boolean
}) => {
  if (!item.reason) return null
  const label = REASON_META[item.reason]?.label || item.reasonLabel || item.reason
  return (
    <span
      className={`ai-lp-badge ai-lp-badge--${item.reason} ${small ? "ai-lp-badge--sm" : ""}`}
    >
      {label}
    </span>
  )
}

const SubjectBadge = ({ subject }: { subject: AiSubject }) => (
  <span className={`ai-lp-badge ai-lp-badge--${subject}`}>
    {SUBJECT_LABEL[subject]}
  </span>
)

interface SourcesTabsProps {
  tab: "past" | "ai"
  onChange: (tab: "past" | "ai") => void
  availableCount: number
}

export const PlanningSourcesTabs = ({
  tab,
  onChange,
  availableCount,
}: SourcesTabsProps) => (
  <div className="ai-lp-tabs" role="tablist">
    <button
      type="button"
      role="tab"
      aria-selected={tab === "past"}
      className={`ai-lp-tab ${tab === "past" ? "ai-lp-tab--active" : ""}`}
      onClick={() => onChange("past")}
    >
      Past session
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={tab === "ai"}
      className={`ai-lp-tab ${tab === "ai" ? "ai-lp-tab--active" : ""}`}
      onClick={() => onChange("ai")}
    >
      <SparkleIcon size={14} />
      AI Lesson Planning
      <span className="ai-lp-count">{availableCount}</span>
    </button>
  </div>
)

interface PlanningSourcesSectionProps {
  ai: AiLessonPlanningController
  studentFirstName: string
  pastSession: React.ReactNode
  onOpenSnapshot?: () => void
}

export const PlanningSourcesSection = ({
  ai,
  studentFirstName,
  pastSession,
  onOpenSnapshot,
}: PlanningSourcesSectionProps) => (
  <div
    className={`kb-child ehp-w plan-sources-column p-0 me-10 border-0 ${
      ai.tab === "ai" ? "plan-sources-column--ai" : ""
    }`}
  >
    <PlanningSourcesTabs
      tab={ai.tab}
      onChange={ai.setTab}
      availableCount={ai.available.length}
    />
    <div className="plan-sources-body">
      {ai.tab === "past" ? (
        pastSession
      ) : (
        <AiLessonPlanningPanel
          ai={ai}
          studentFirstName={studentFirstName}
          onOpenSnapshot={onOpenSnapshot}
        />
      )}
    </div>
  </div>
)

interface RecommendationCardProps {
  item: AiRecommendation
  isNow?: boolean
  busy?: boolean
  onPlace: (id: string, target: AiPlanTarget) => void
  onDismiss: (id: string, reason: string) => void
}

const TopPickCard = ({
  item,
  isNow,
  busy,
  onPlace,
  onDismiss,
}: RecommendationCardProps) => {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <div className="ai-lp-card">
      <div className="ai-lp-card-body">
        <div className="ai-lp-badges">
          <span className="ai-lp-rank">#{item.rank}</span>
          <ReasonBadge item={item} />
          <SubjectBadge subject={item.subject} />
          <span className="ai-lp-type">{contentTypeLabel(item.contentType)}</span>
          {isNow ? <span className="ai-lp-badge ai-lp-badge--now">NOW</span> : null}
        </div>
        <span className="ai-lp-card-title">{item.title}</span>
        <span className="ai-lp-meta">{itemMeta(item)}</span>

        {menuOpen ? (
          <div className="ai-lp-skip-menu">
            <span className="ai-lp-skip-title">
              Why skip this one? The next recommendation takes its place.
            </span>
            <div className="ai-lp-skip-options">
              {SKIP_REASONS.map(reason => (
                <button
                  type="button"
                  key={reason}
                  className="ai-lp-chip"
                  onClick={() => {
                    setMenuOpen(false)
                    onDismiss(item.id, reason)
                  }}
                >
                  {reason}
                </button>
              ))}
              <button
                type="button"
                className="ai-lp-link-btn"
                onClick={() => setMenuOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        <div className="ai-lp-actions">
          <button
            type="button"
            className="ai-lp-btn-add"
            disabled={busy}
            onClick={() => onPlace(item.id, "lesson")}
          >
            {busy ? "Adding…" : "+ Lesson Plan"}
          </button>
          <button
            type="button"
            className="ai-lp-btn-add"
            disabled={busy}
            onClick={() => onPlace(item.id, "homework")}
          >
            {busy ? "Adding…" : "+ Homework"}
          </button>
          <button
            type="button"
            className="ai-lp-btn-ghost"
            disabled={busy}
            onClick={() => setMenuOpen(true)}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  )
}

interface AiLessonPlanningPanelProps {
  ai: AiLessonPlanningController
  studentFirstName: string
  onOpenSnapshot?: () => void
}

export const AiLessonPlanningPanel = ({
  ai,
  studentFirstName,
  onOpenSnapshot,
}: AiLessonPlanningPanelProps) => {
  const [showAll, setShowAll] = useState(false)
  const [subjectFilter, setSubjectFilter] = useState<SubjectFilter>("both")

  const matchesSubject = (subject: AiSubject) =>
    subjectFilter === "both" || subject === subjectFilter
  const visibleSubjects = ai.subjects.filter(matchesSubject)
  const visibleTopPicks = ai.topPicks.filter(item => matchesSubject(item.subject))
  const visibleRest = ai.rest.filter(item => matchesSubject(item.subject))

  if (!ai.ready) {
    return (
      <div className="ai-lp-panel">
        <div className="ai-lp-empty">
          Open a student session to load recommendations.
        </div>
      </div>
    )
  }

  const refreshedText = ai.subjectStates
    .filter(state => state.executedAt)
    .map(
      state =>
        `${SUBJECT_LABEL[state.subject]} ${moment(state.executedAt).format("DD MMM HH:mm")}`
    )
    .join(" · ")
  const loadedCount = ai.subjectStates.filter(s => s.status === "success").length
  const firstLoad = ai.isLoading && loadedCount === 0

  return (
    <div className="ai-lp-panel">
      <div className="ai-lp-head">
        <div className="ai-lp-head-text">
          <h2 className="ai-lp-title">
            Recommended for {studentFirstName || "this student"}
          </h2>
          <span className="ai-lp-sub">
            {subjectsLabel(visibleSubjects)}
            {visibleSubjects.length > 1 ? " together" : ""}
            {refreshedText ? ` · ${refreshedText}` : ""}
          </span>
        </div>
        <div className="ai-lp-section-actions">
        {onOpenSnapshot ? (
          <button type="button" className="ai-lp-snapshot-btn" onClick={onOpenSnapshot}>
            <SnapshotIcon />
            Child snapshot
          </button>
        ) : null}
        <button
          type="button"
          className="ai-lp-icon-btn"
          aria-label="Refresh recommendations"
          title="Refresh recommendations"
          disabled={ai.isLoading}
          onClick={ai.refresh}
        >
          {ai.isLoading ? (
            <span className="spinner-border spinner-border-sm" role="status" />
          ) : (
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#5B6480"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M20 11a8 8 0 1 0-2.3 5.7" />
              <path d="M20 5v6h-6" />
            </svg>
          )}
        </button>
        </div>
      </div>

      <div className="ai-lp-filters">
        <div className="ai-lp-filter-group">
          <span className="ai-lp-filter-label">Subject</span>
          {(
            [
              ["both", "Both"],
              ["math", "Maths"],
              ["english", "English"],
            ] as Array<[SubjectFilter, string]>
          ).map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={`ai-lp-chip ${subjectFilter === value ? "ai-lp-chip--active" : ""}`}
              onClick={() => setSubjectFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {ai.subjectStates
        .filter(state => state.status === "error" && matchesSubject(state.subject))
        .map(state => (
          <div className="ai-lp-alert" key={state.subject}>
            <span>
              Couldn&apos;t load {SUBJECT_LABEL[state.subject]} recommendations:{" "}
              {state.error}
            </span>
            <button type="button" className="ai-lp-link-btn" onClick={ai.refresh}>
              Retry
            </button>
          </div>
        ))}

      {firstLoad ? (
        <div className="ai-lp-empty">
          <span className="spinner-border spinner-border-sm me-2" role="status" />
          Loading recommendations…
        </div>
      ) : (
        <>
          <div className="ai-lp-legend">
            {REASON_ORDER.map(reason => (
              <div className={`ai-lp-legend-item ai-lp-legend-item--${reason}`} key={reason}>
                <span className="ai-lp-legend-label">{REASON_META[reason].label}</span>
                <span className="ai-lp-legend-hint">{REASON_META[reason].hint}</span>
              </div>
            ))}
          </div>

          <div className="ai-lp-section-head">
            <div className="ai-lp-head-text">
              <span className="ai-lp-section-title">Top picks for this session</span>
              <span className="ai-lp-sub">
                Add them to the Lesson Plan or Homework
              </span>
            </div>
            <div className="ai-lp-section-actions">
              <button
                type="button"
                className="ai-lp-btn-outline"
                onClick={() => setShowAll(true)}
              >
                Show all ({visibleRest.length})
              </button>
              <button
                type="button"
                className="ai-lp-btn-primary"
                disabled={visibleTopPicks.length === 0 || ai.assigningIds.length > 0}
                onClick={() =>
                  ai.assign(
                    visibleTopPicks.map(item => item.id),
                    "lesson"
                  )
                }
              >
                Add all to Lesson Plan
              </button>
            </div>
          </div>

          {visibleTopPicks.length === 0 ? (
            <div className="ai-lp-empty ai-lp-empty--dashed">
              {Object.keys(ai.byId).length === 0
                ? "No recommendations returned for this student yet."
                : "All top picks are planned or dismissed. Open the full list below for more."}
            </div>
          ) : (
            <div className="ai-lp-card-list">
              {visibleTopPicks.map(item => (
                <TopPickCard
                  key={item.id}
                  item={item}
                  isNow={ai.nowIds.has(item.id)}
                  busy={ai.assigningIds.indexOf(item.id) >= 0}
                  onPlace={(id, target) => {
                    void ai.assign([id], target)
                  }}
                  onDismiss={ai.dismiss}
                />
              ))}
            </div>
          )}

          <button
            type="button"
            className="ai-lp-show-all"
            onClick={() => setShowAll(true)}
          >
            Show all recommendations ({visibleRest.length} more)
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#233A8C"
              strokeWidth="2.2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M14 4h6v6M20 4l-8 8M10 6H5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-5" />
            </svg>
          </button>

          {ai.dismissedItems.length > 0 ? (
            <div className="ai-lp-dismissed">
              <span className="ai-lp-dismissed-title">
                Skipped by tutor ({ai.dismissedItems.length})
              </span>
              <span className="ai-lp-sub">
                Hidden from this session&apos;s picks. Undo to bring one back.
              </span>
              {ai.dismissedItems.map(({ item, reason }) => (
                <div className="ai-lp-dismissed-row" key={item.id}>
                  <span className="ai-lp-dismissed-name">{item.title}</span>
                  <span className="ai-lp-dismissed-reason">{reason}</span>
                  <button
                    type="button"
                    className="ai-lp-link-btn"
                    onClick={() => ai.undoDismiss(item.id)}
                  >
                    Undo
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}

      <AllRecommendationsModal
        isOpen={showAll}
        onClose={() => setShowAll(false)}
        ai={ai}
        studentFirstName={studentFirstName}
        subjectFilter={subjectFilter}
        onSubjectFilter={setSubjectFilter}
        visibleRest={visibleRest}
      />
    </div>
  )
}

interface AllRecommendationsModalProps {
  isOpen: boolean
  onClose: () => void
  ai: AiLessonPlanningController
  studentFirstName: string
  subjectFilter: SubjectFilter
  onSubjectFilter: (filter: SubjectFilter) => void
  visibleRest: AiRecommendation[]
}

const AllRecommendationsModal = ({
  isOpen,
  onClose,
  ai,
  studentFirstName,
  subjectFilter,
  onSubjectFilter,
  visibleRest,
}: AllRecommendationsModalProps) => {
  const [reasonFilter, setReasonFilter] = useState<ReasonFilter>("all")

  const reasonsInData = Array.from(
    new Set(visibleRest.map(item => item.reason || "other"))
  )
  const groupOrder = [
    ...REASON_ORDER,
    ...reasonsInData.filter(reason => !REASON_ORDER.includes(reason)),
  ]

  const groups = groupOrder
    .filter(reason => reasonFilter === "all" || reasonFilter === reason)
    .map(reason => {
      const items = visibleRest.filter(
        item => (item.reason || "other") === reason
      )
        .sort(
          (a, b) => a.rank - b.rank || (a.subject < b.subject ? -1 : 1)
        )
      const sample = items[0]
      return {
        reason,
        label:
          REASON_META[reason]?.label || sample?.reasonLabel || "Other",
        hint: REASON_META[reason]?.hint.toLowerCase() || "",
        items,
      }
    })
    .filter(group => group.items.length > 0)

  const reasonChips: Array<[ReasonFilter, string]> = [
    ["all", "All"],
    ["progress", "Progress"],
    ["preference", "Preference"],
    ["relearn", "Relearn"],
  ]
  const subjectChips: Array<[SubjectFilter, string]> = [
    ["both", "Both"],
    ["math", "Maths"],
    ["english", "English"],
  ]

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="xl"
      centered
      scrollable
      contentClassName="ai-lp-modal"
      aria-label="All recommendations"
    >
      <div className="ai-lp-modal-head">
        <div className="ai-lp-modal-title-row">
          <div className="ai-lp-head-text">
            <h2 className="ai-lp-modal-title">
              All recommendations for {studentFirstName || "this student"}
            </h2>
            <span className="ai-lp-sub">
              {visibleRest.length} more beyond the top picks ·{" "}
              {subjectFilter === "both"
                ? "Maths & English"
                : SUBJECT_LABEL[subjectFilter]}{" "}
              · ranked by the AI
            </span>
          </div>
          <button
            type="button"
            className="ai-lp-icon-btn ai-lp-icon-btn--lg"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon size={14} />
          </button>
        </div>
        <div className="ai-lp-filters">
          <div className="ai-lp-filter-group">
            <span className="ai-lp-filter-label">Reason</span>
            {reasonChips.map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={`ai-lp-chip ${reasonFilter === value ? "ai-lp-chip--active" : ""}`}
                onClick={() => setReasonFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="ai-lp-filter-group">
            <span className="ai-lp-filter-label">Subject</span>
            {subjectChips.map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={`ai-lp-chip ${subjectFilter === value ? "ai-lp-chip--active" : ""}`}
                onClick={() => onSubjectFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="ai-lp-modal-body">
        {groups.length === 0 ? (
          <div className="ai-lp-empty">Nothing left for this filter.</div>
        ) : (
          groups.map(group => (
            <div className="ai-lp-group" key={group.reason}>
              <div className="ai-lp-group-head">
                <span className={`ai-lp-badge ai-lp-badge--lg ai-lp-badge--${group.reason}`}>
                  {group.label}
                </span>
                <span className="ai-lp-sub">
                  {group.items.length} {group.items.length === 1 ? "item" : "items"}
                  {group.hint ? ` · ${group.hint}` : ""}
                </span>
              </div>
              <div className="ai-lp-grid">
                {group.items.map(item => (
                  <div className="ai-lp-grid-card" key={item.id}>
                    <div className="ai-lp-badges">
                      <span className="ai-lp-rank">#{item.rank}</span>
                      <SubjectBadge subject={item.subject} />
                      <span className="ai-lp-type">{contentTypeLabel(item.contentType)}</span>
                    </div>
                    <span className="ai-lp-card-title">{item.title}</span>
                    <span className="ai-lp-meta">{itemMeta(item)}</span>
                    <div className="ai-lp-actions">
                      <button
                        type="button"
                        className="ai-lp-btn-add"
                        disabled={ai.assigningIds.indexOf(item.id) >= 0}
                        onClick={() => {
                          void ai.assign([item.id], "lesson")
                        }}
                      >
                        + Lesson Plan
                      </button>
                      <button
                        type="button"
                        className="ai-lp-btn-add"
                        disabled={ai.assigningIds.indexOf(item.id) >= 0}
                        onClick={() => {
                          void ai.assign([item.id], "homework")
                        }}
                      >
                        + Homework
                      </button>
                      <button
                        type="button"
                        className="ai-lp-btn-ghost"
                        disabled={ai.assigningIds.indexOf(item.id) >= 0}
                        onClick={() => ai.dismiss(item.id, "Not now")}
                      >
                        Not now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="ai-lp-modal-foot">
        <span>
          Added: <b>{ai.lessonCount}</b> in Lesson Plan ·{" "}
          <b>{ai.homeworkCount}</b> in Homework
        </span>
        <button type="button" className="ai-lp-btn-done" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  )
}
